import type { Selection } from 'd3-selection';

// 'ingredients'/'preparation' used to be two separate tabs; they're now
// one combined "Elaboració" tab showing both sections together (see
// HomePage.ts's tab-elaboracio wrapper).
export type TabId = 'graph' | 'list' | 'elaboracio' | 'timeline';

interface TabDef {
  id: TabId;
  label: string;
  icon: string;
}

// ⚗ is the same alembic glyph used everywhere else for "elaboració" (see
// ListNodeItem.ts/EditToolbar.ts/GraphVisualizer.ts's [⚗]/[⚗ׯ] buttons) —
// reused here rather than picked fresh. ◷ matches the clock glyph
// durationBadgeHtml() uses for time — a plain Geometric Shapes character,
// not an emoji-presentation one (unlike ⏱, which most fonts render as a
// colored emoji by default). '≡' (Mathematical Operators), not '☰'
// (Miscellaneous Symbols) — the latter is missing from enough system/
// monospace font fallback chains (notably on Firefox/Windows) to render
// as nothing at all; '≡' reads identically as a hamburger/list glyph and
// is covered far more reliably (see SiteNav.ts's own hamburger button,
// fixed the same way).
const ALL_TABS: TabDef[] = [
  { id: 'graph', label: 'Graf', icon: '⬡' },
  { id: 'list', label: 'Llista', icon: '≡' },
  { id: 'elaboracio', label: 'Elaboració', icon: '⚗' },
  { id: 'timeline', label: 'Cronologia', icon: '◷' },
];

/**
 * TabSelector — renders tab buttons for narrow-screen recipe view.
 *
 * Hidden on wide screens via CSS (`lg:hidden`). Manages active tab state
 * and fires an `onTabChange` callback when the user switches tabs.
 */
export class TabSelector {
  private container: Selection<any, any, any, any>;
  private activeTab: TabId = 'graph';
  private visibleTabs: Set<TabId> = new Set(['graph']);
  private onTabChangeCallback: ((tab: TabId) => void) | null = null;

  constructor(container: Selection<any, any, any, any>) {
    this.container = container;
    // Preserve existing classes (like px-4), just add tab-specific ones.
    // flex-wrap lets the buttons spill onto a second line on narrow
    // screens instead of overflowing/shrinking when they don't all fit.
    this.container.classed('flex', true).classed('flex-wrap', true).classed('gap-2', true).classed('mb-4', true).classed('lg:hidden', true);
    this.render();
  }

  /** Render the tab buttons. */
  private render(): void {
    this.container.selectAll('*').remove();

    this.container.selectAll<HTMLButtonElement, TabDef>('button')
      .data(ALL_TABS)
      .enter()
      .append('button')
      .attr('data-tab', (d) => d.id)
      .attr('title', (d) => d.label)
      .attr('aria-label', (d) => d.label)
      .on('click', (_event, d) => {
        if (!this.visibleTabs.has(d.id)) return;
        this.setActive(d.id);
      });

    this.refreshButtons();
  }

  /**
   * Re-apply class, content, and visibility to every existing button —
   * called after render() and on every state change (active tab or
   * visible-tabs set) instead of rebuilding the DOM.
   */
  private refreshButtons(): void {
    this.container.selectAll<HTMLButtonElement, TabDef>('button')
      .attr('class', (d) => this.tabButtonClass(d.id))
      .style('display', (d) => this.visibleTabs.has(d.id) ? null : 'none')
      .html((d) => this.tabButtonHtml(d));
  }

  /**
   * Icon-only when inactive, icon + label when active — same convention
   * NodeVisualizer's own edit-panel tabs use (see renderEditableContent's
   * activateTab()), applied here so the two tab UIs read as one family.
   */
  private tabButtonHtml(tab: TabDef): string {
    if (tab.id === this.activeTab) {
      return `${tab.icon} <span class="text-sm font-medium">${tab.label}</span>`;
    }
    return tab.icon;
  }

  /**
   * Compute CSS classes for a tab button. Always includes the stable
   * `tab-selector-btn` class (alongside the Tailwind utility classes,
   * which change with active state) — GraphVisualizer's pick-node outside-
   * click handler hooks into it so switching tabs mid-pick doesn't cancel
   * the pick. Underline style matches NodeVisualizer's edit-panel tabs
   * (border-b-2, text-lg icon) rather than this app's other pill-button
   * convention, per the same "read as one tab family" goal above.
   */
  private tabButtonClass(tabId: TabId): string {
    const base = 'tab-selector-btn px-3 py-1.5 text-lg leading-none border-b-2 transition-colors whitespace-nowrap';
    if (tabId === this.activeTab) {
      return `${base} border-[var(--accent)] text-[var(--accent)]`;
    }
    return `${base} border-transparent text-[#888] hover:text-[var(--accent)]`;
  }

  /**
   * Show browse tabs (graph and list) when no recipe is selected.
   * Does not change the active tab.
   */
  showBrowseTabs(): void {
    this.visibleTabs = new Set(['graph', 'list']);
    // If current active tab is not in visible tabs, switch to 'graph'
    if (!this.visibleTabs.has(this.activeTab)) {
      this.setActive('graph');
    } else {
      // Just refresh visibility
      this.refreshButtons();
    }
  }

  /**
   * Show all tabs (recipe loaded). Does not change the active tab — going
   * from browse to all only ever *adds* tabs, so whatever's active stays
   * valid.
   */
  showAllTabs(): void {
    this.visibleTabs = new Set(['graph', 'list', 'elaboracio', 'timeline']);
    // Refresh button display styles to match — without this, the newly
    // visible tabs stay hidden in the DOM until some unrelated click (e.g.
    // switching tabs) happens to re-render them.
    this.refreshButtons();
  }

  /**
   * Set the active tab and re-render button styles.
   */
  setActive(tab: TabId): void {
    if (!this.visibleTabs.has(tab)) return;
    this.activeTab = tab;
    this.refreshButtons();
    this.onTabChangeCallback?.(tab);
  }

  /**
   * Register a callback fired when the active tab changes.
   */
  onTabChange(callback: (tab: TabId) => void): void {
    this.onTabChangeCallback = callback;
  }

  /**
   * Get the currently active tab.
   */
  getActive(): TabId {
    return this.activeTab;
  }

  /**
   * Return the set of currently visible tab IDs.
   */
  getVisibleTabs(): Set<TabId> {
    return this.visibleTabs;
  }

  /**
   * Remove all content and event listeners.
   */
  destroy(): void {
    this.container.selectAll('*').remove();
    this.onTabChangeCallback = null;
  }
}
